// Spec 47 approval-ux — cmp_approval_decision_card / decision_card_v1.
//
// "The atomic approval surface. One approval = one decision card. Used
// in the inbox, in notification detail sheets, on mobile, and as the
// target of any 'open approval' intent from any module." (spec 47
// decision_card_v1)
//
// Compound API: slot composition. The card itself owns layout + a11y;
// children fill the slots (subject_kind_chip, summary_line, etc.).
// Module renderers (PTO / expense / comp / AI staged change) plug in
// the same slots so every approval feels identical to the approver.
//
// Spec 47 §feat_aux_decision_card_everywhere AC: "MUST render via
// decision_card_v1 or the lint rule approval_ux_subject_kind_coverage
// fails."
//
// a11y: card is <section> with aria-labelledby pointing to the
// summary_line. Action button labels INCLUDE the subject ("Approve PTO
// request from Jamie Lin", not just "Approve") — caller passes
// subjectAriaSummary; component composes the full label.

import {
  forwardRef,
  useId,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export interface DecisionCardProps extends HTMLAttributes<HTMLElement> {
  /** Subject summary used in action button aria-labels (e.g., "PTO request from Jamie Lin"). */
  readonly subjectAriaSummary: string;
  readonly children?: ReactNode;
  /** Optional break-glass banner element (rendered at top). */
  readonly breakGlassBanner?: ReactNode;
  /** Optional delegation byline (rendered at foot). */
  readonly delegationByline?: ReactNode;
}

/**
 * Root card. Compose with <DecisionCard.Header>, <Body>, <Footer>,
 * <Actions>. The component itself only owns the <section> wrapper +
 * a11y identifier wiring.
 */
const DecisionCardRoot = forwardRef<HTMLElement, DecisionCardProps>(
  (
    {
      subjectAriaSummary,
      breakGlassBanner,
      delegationByline,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const summaryId = useId();
    return (
      <section
        ref={ref}
        aria-labelledby={summaryId}
        data-component="decision-card"
        data-subject={subjectAriaSummary}
        className={cx("nai-decision-card", className)}
        {...rest}
      >
        {breakGlassBanner !== undefined && (
          <div data-component="decision-card-break-glass" className="nai-decision-card-break-glass">
            {breakGlassBanner}
          </div>
        )}
        <DecisionCardSummaryIdContext.Provider value={summaryId}>
          {children}
        </DecisionCardSummaryIdContext.Provider>
        {delegationByline !== undefined && (
          <div data-component="decision-card-delegation" className="nai-decision-card-delegation">
            {delegationByline}
          </div>
        )}
      </section>
    );
  },
);
DecisionCardRoot.displayName = "DecisionCard";

// ==========================================================================
// SUMMARY-ID CONTEXT — lets child Summary set the aria-labelledby target
// ==========================================================================

import { createContext, useContext } from "react";

const DecisionCardSummaryIdContext = createContext<string | null>(null);

function useSummaryId(): string | null {
  return useContext(DecisionCardSummaryIdContext);
}

// ==========================================================================
// HEADER
// ==========================================================================

export interface DecisionCardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  readonly subjectKindChip?: ReactNode;
  readonly slaPill?: ReactNode;
  readonly children?: ReactNode;
}

const DecisionCardHeader = forwardRef<HTMLDivElement, DecisionCardHeaderProps>(
  ({ subjectKindChip, slaPill, className, children, ...rest }, ref) => (
    <header
      ref={ref}
      data-component="decision-card-header"
      className={cx("nai-decision-card-header", className)}
      {...rest as HTMLAttributes<HTMLElement>}
    >
      {subjectKindChip}
      <div className="nai-decision-card-header-content">{children}</div>
      {slaPill}
    </header>
  ),
);
DecisionCardHeader.displayName = "DecisionCard.Header";

// ==========================================================================
// SUMMARY (the aria-labelledby target — one per card)
// ==========================================================================

export interface DecisionCardSummaryProps extends HTMLAttributes<HTMLHeadingElement> {
  readonly children?: ReactNode;
}

const DecisionCardSummary = forwardRef<HTMLHeadingElement, DecisionCardSummaryProps>(
  ({ className, children, ...rest }, ref) => {
    const id = useSummaryId();
    return (
      <h2
        ref={ref}
        id={id ?? undefined}
        data-component="decision-card-summary"
        className={cx("nai-decision-card-summary", className)}
        {...rest}
      >
        {children}
      </h2>
    );
  },
);
DecisionCardSummary.displayName = "DecisionCard.Summary";

// ==========================================================================
// BODY (impact callout + chain visualizer + diff shortcut + evidence)
// ==========================================================================

export interface DecisionCardBodyProps extends HTMLAttributes<HTMLDivElement> {
  readonly children?: ReactNode;
}

const DecisionCardBody = forwardRef<HTMLDivElement, DecisionCardBodyProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      ref={ref}
      data-component="decision-card-body"
      className={cx("nai-decision-card-body", className)}
      {...rest}
    >
      {children}
    </div>
  ),
);
DecisionCardBody.displayName = "DecisionCard.Body";

// ==========================================================================
// FOOTER
// ==========================================================================

export interface DecisionCardFooterProps extends HTMLAttributes<HTMLDivElement> {
  readonly children?: ReactNode;
}

const DecisionCardFooter = forwardRef<HTMLDivElement, DecisionCardFooterProps>(
  ({ className, children, ...rest }, ref) => (
    <footer
      ref={ref}
      data-component="decision-card-footer"
      className={cx("nai-decision-card-footer", className)}
      {...rest as HTMLAttributes<HTMLElement>}
    >
      {children}
    </footer>
  ),
);
DecisionCardFooter.displayName = "DecisionCard.Footer";

// ==========================================================================
// ACTIONS — Approve / Reject / Comment with subject-included aria-labels
// ==========================================================================

export interface DecisionCardActionsProps {
  readonly subjectAriaSummary: string;
  readonly approveLabel?: string;
  readonly rejectLabel?: string;
  readonly commentLabel?: string;
  readonly onApprove: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly onReject: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly onComment?: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly disabled?: boolean;
}

const DecisionCardActions = forwardRef<HTMLDivElement, DecisionCardActionsProps>(
  (
    {
      subjectAriaSummary,
      approveLabel = "Approve",
      rejectLabel = "Reject",
      commentLabel = "Add comment",
      onApprove,
      onReject,
      onComment,
      disabled,
    },
    ref,
  ) => (
    <div
      ref={ref}
      data-component="decision-card-actions"
      className="nai-decision-card-actions"
      role="group"
      aria-label={`Decision actions for ${subjectAriaSummary}`}
    >
      <button
        type="button"
        data-component="decision-card-action-approve"
        data-action-kind="approve"
        className="nai-decision-card-action-approve"
        disabled={disabled === true}
        aria-label={`${approveLabel} ${subjectAriaSummary}`}
        onClick={onApprove}
      >
        {approveLabel}
      </button>
      <button
        type="button"
        data-component="decision-card-action-reject"
        data-action-kind="reject"
        className="nai-decision-card-action-reject"
        disabled={disabled === true}
        aria-label={`${rejectLabel} ${subjectAriaSummary}`}
        onClick={onReject}
      >
        {rejectLabel}
      </button>
      {onComment !== undefined && (
        <button
          type="button"
          data-component="decision-card-action-comment"
          data-action-kind="comment"
          className="nai-decision-card-action-comment"
          disabled={disabled === true}
          aria-label={`${commentLabel} on ${subjectAriaSummary}`}
          onClick={onComment}
        >
          {commentLabel}
        </button>
      )}
    </div>
  ),
);
DecisionCardActions.displayName = "DecisionCard.Actions";

// ==========================================================================
// COMPOUND EXPORT
// ==========================================================================

type DecisionCardCompound = typeof DecisionCardRoot & {
  readonly Header: typeof DecisionCardHeader;
  readonly Summary: typeof DecisionCardSummary;
  readonly Body: typeof DecisionCardBody;
  readonly Footer: typeof DecisionCardFooter;
  readonly Actions: typeof DecisionCardActions;
};

export const DecisionCard: DecisionCardCompound = Object.assign(DecisionCardRoot, {
  Header: DecisionCardHeader,
  Summary: DecisionCardSummary,
  Body: DecisionCardBody,
  Footer: DecisionCardFooter,
  Actions: DecisionCardActions,
});
